import type { AnchorHTMLAttributes, FC, MouseEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { usePageTransition } from '@/components/motion/page-transition'
import { useLenis } from '@/components/motion/smooth-scroll'

interface TransitionLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string
    children: ReactNode
}

// Internal link that plays the page curtain. Same-page links (with or without
// a hash) scroll smoothly instead of reloading the route.
const TransitionLink: FC<TransitionLinkProps> = ({ href, onClick, children, ...rest }): ReactNode => {
    const router = useRouter()
    const navigate = usePageTransition()
    const lenis = useLenis()

    const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
        onClick?.(event)

        if (event.defaultPrevented) return
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        if (rest.target && rest.target !== '_self') return

        const [pathAndQuery, hash] = href.split('#')
        const currentPath = router.asPath.split(/[?#]/)[0]
        const targetPath = (pathAndQuery || currentPath).split('?')[0]
        const sameQuery = !pathAndQuery?.includes('?') || router.asPath.split('#')[0] === pathAndQuery

        if (targetPath === currentPath && sameQuery) {
            event.preventDefault()

            const target = hash ? document.getElementById(hash) : null

            if (target) {
                if (lenis) lenis.scrollTo(target, { offset: -16 })
                else target.scrollIntoView({ behavior: 'smooth' })
            }
            else if (!hash) {
                if (lenis) lenis.scrollTo(0)
                else window.scrollTo({ top: 0, behavior: 'smooth' })
            }

            return
        }

        if (!navigate) return

        event.preventDefault()
        navigate(href)
    }

    return (
        <Link href={href} onClick={handleClick} {...rest}>
            {children}
        </Link>
    )
}

export default TransitionLink
